import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { changeFoodStatus, getPausedSinceAPI } from "../../utils/ordersControlar";
import { useGlobalOrder } from "../../utils/GlobalStateContext";
import { collectPausedProducts, formatPausedSince } from "./pausedProducts";

const CONFIRM_TIMEOUT_MS = 3000;

const statusStyle = {
    2: { label: "暫停", background: "#FFF4E5", color: "#B45309", border: "rgba(245,124,0,0.35)", tag: "#F57C00" },
    3: { label: "停售", background: "#FFF0F0", color: "#B30000", border: "rgba(179,0,0,0.28)", tag: "#B30000" },
};

// 暫停販售品項清單：顯示已暫停多久，並可直接恢復銷售（點兩下確認，避免內場誤觸）
// statuses 可只列特定狀態（如只看停售）；onRestored 給有自己商品資料的頁面（餐點管理）同步重抓
function PausedProductsList({ emptyText = "目前所有品項皆正常販售", size = "normal", statuses, onRestored }) {
    const { systemData, refreshProductData } = useGlobalOrder();
    const statusKey = statuses ? statuses.join(",") : "";
    const pausedProducts = useMemo(() => {
        const list = collectPausedProducts(systemData);
        return statusKey ? list.filter((item) => statusKey.split(",").includes(String(item.status))) : list;
    }, [systemData, statusKey]);
    const namesKey = pausedProducts.map((item) => `${item.name}:${item.status}`).join("|");

    const [pausedSince, setPausedSince] = useState({});
    const [nowMs, setNowMs] = useState(Date.now());
    const [confirmName, setConfirmName] = useState(null);
    const [restoringName, setRestoringName] = useState(null);
    const confirmTimerRef = useRef(null);

    // 暫停清單變動才重抓開始時間
    useEffect(() => {
        if (!namesKey) return undefined;
        let cancelled = false;
        getPausedSinceAPI().then((list) => {
            if (!cancelled && list) setPausedSince(list);
        });
        return () => {
            cancelled = true;
        };
    }, [namesKey]);

    useEffect(() => {
        const timerId = setInterval(() => setNowMs(Date.now()), 60000);
        return () => clearInterval(timerId);
    }, []);

    useEffect(() => () => clearTimeout(confirmTimerRef.current), []);

    const handleRestore = async (name) => {
        if (restoringName) return;
        if (confirmName !== name) {
            setConfirmName(name);
            clearTimeout(confirmTimerRef.current);
            confirmTimerRef.current = setTimeout(() => setConfirmName(null), CONFIRM_TIMEOUT_MS);
            return;
        }
        clearTimeout(confirmTimerRef.current);
        setConfirmName(null);
        setRestoringName(name);
        try {
            await changeFoodStatus({ name, status: 1 });
            toast.success(`「${name}」已恢復銷售`);
            await Promise.all([refreshProductData(), onRestored?.()]);
        } catch (error) {
            // changeFoodStatus 已提示錯誤
        } finally {
            setRestoringName(null);
        }
    };

    if (!pausedProducts.length) {
        return (
            <Box sx={{ width: "100%", py: 2, textAlign: "center", color: "#2F7D4A", fontWeight: 800, fontSize: 15 }}>
                {emptyText}
            </Box>
        );
    }

    const large = size === "large";

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: large ? "center" : "flex-start" }}>
            {pausedProducts.map((item) => {
                const style = statusStyle[item.status] || statusStyle[3];
                const sinceText = formatPausedSince(pausedSince[item.name], nowMs);
                const confirming = confirmName === item.name;
                const restoring = restoringName === item.name;
                return (
                    <Box
                        key={item.name}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            pl: 1.5,
                            pr: 0.75,
                            py: 0.75,
                            borderRadius: "14px",
                            backgroundColor: style.background,
                            border: "1px solid",
                            borderColor: style.border,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    color: style.color,
                                    fontSize: large ? 18 : 15,
                                    fontWeight: 800,
                                }}
                            >
                                {item.name}
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 800,
                                        px: 0.75,
                                        borderRadius: "6px",
                                        backgroundColor: style.tag,
                                        color: "#FFF",
                                        lineHeight: "18px",
                                    }}
                                >
                                    {style.label}
                                </Box>
                            </Box>
                            {sinceText ? (
                                <Typography sx={{ color: "#536073", fontSize: large ? 13 : 12, fontWeight: 700, lineHeight: "18px" }}>
                                    {sinceText}
                                </Typography>
                            ) : null}
                        </Box>
                        <Button
                            size="small"
                            onClick={() => handleRestore(item.name)}
                            disabled={Boolean(restoringName)}
                            sx={{
                                flexShrink: 0,
                                minWidth: 0,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "999px",
                                fontSize: 13,
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                                color: confirming ? "#FFF" : "#2F7D4A",
                                backgroundColor: confirming ? "#2F7D4A" : "#FFF",
                                border: "1px solid rgba(47,125,74,0.45)",
                                "&:hover": {
                                    backgroundColor: confirming ? "#256B3D" : "#EEF7F1",
                                },
                            }}
                        >
                            {restoring ? <CircularProgress size={14} sx={{ color: "#2F7D4A" }} /> : confirming ? "確認恢復？" : "恢復銷售"}
                        </Button>
                    </Box>
                );
            })}
        </Box>
    );
}

export default PausedProductsList;
